import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { LoaderService } from '../services/loader.service';
import {
    StaffDisputeAttachment,
    StaffDisputeDetail,
    StaffDisputeService
} from '../services/staff-dispute.service';
import { DisputeSummaryHeaderComponent } from './components/dispute-summary-header.component';
import { DisputeItemsCardComponent } from './components/dispute-items-card.component';
import { DisputeAttachmentCardComponent } from './components/dispute-attachment-card.component';
import { DisputeTimelineComponent } from './components/dispute-timeline.component';
import {
    DisputeReviewDrawerComponent,
    ReviewStatusOption
} from './components/dispute-review-drawer.component';
import {
    CreditNoteDialogConfirm,
    DisputeCreditNoteDialogComponent
} from './components/dispute-credit-note-dialog.component';
import { canGenerateCreditNote } from './dispute.utils';
import { OrderDraftService } from '../services/order-draft.service';

@Component({
    selector: 'app-dispute-review-detail',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        ToolbarModule,
        DisputeSummaryHeaderComponent,
        DisputeItemsCardComponent,
        DisputeAttachmentCardComponent,
        DisputeTimelineComponent,
        DisputeReviewDrawerComponent,
        DisputeCreditNoteDialogComponent
    ],
    providers: [MessageService],
    templateUrl: './dispute-review-detail.component.html',
    styleUrl: './dispute-review-detail.component.scss'
})
export class DisputeReviewDetailComponent implements OnInit, OnDestroy {
    claimNumber = '';
    detail: StaffDisputeDetail | null = null;
    attachmentPreviewUrls = new Map<number, string>();

    drawerVisible = false;
    creditNoteDialogVisible = false;

    scope = {
        companyId: '',
        divisionId: '',
        whsId: ''
    };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly inMemoryStorage: InMemoryStorageService,
        private readonly disputeService: StaffDisputeService,
        private readonly messageService: MessageService,
        private readonly loaderService: LoaderService,
        private readonly orderDraftService: OrderDraftService
    ) {}

    ngOnInit(): void {
        this.scope = {
            companyId: this.inMemoryStorage.get('company') || '',
            divisionId: this.inMemoryStorage.get('division') || '',
            whsId:
                this.inMemoryStorage.get('warehouseId') ||
                this.inMemoryStorage.get('whsId') ||
                this.inMemoryStorage.get('warehouse') ||
                ''
        };
        this.claimNumber = this.route.snapshot.paramMap.get('claimNumber') || '';
        if (this.claimNumber) {
            this.load();
        } else {
            this.backToList();
        }
        this.route.paramMap.subscribe((params) => {
            const claim = params.get('claimNumber') || '';
            if (claim && claim !== this.claimNumber) {
                this.claimNumber = claim;
                this.load();
            }
        });
    }

    ngOnDestroy(): void {
        this.revokeAttachmentPreviews();
    }

    backToList(): void {
        this.router.navigate(['/dispute-review']);
    }

    load(): void {
        if (!this.claimNumber) {
            this.backToList();
            return;
        }

        this.revokeAttachmentPreviews();
        this.detail = null;
        this.loaderService.show('Loading dispute...', 'Please wait...');
        this.disputeService
            .getClaim(this.claimNumber, this.scope)
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (detail) => {
                    this.detail = detail;
                    this.loadAttachmentPreviews();
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Failed to load dispute',
                        detail: err?.error?.message || err?.error || 'Please retry.'
                    });
                }
            });
    }

    canGenerateCreditNote(): boolean {
        return canGenerateCreditNote(this.detail, this.scope.divisionId);
    }

    openCreditNoteDialog(): void {
        if (!this.detail || !this.canGenerateCreditNote()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Not available',
                detail: 'Credit notes can only be generated for approved division 400 disputes without an existing link.'
            });
            return;
        }
        this.creditNoteDialogVisible = true;
    }

    onCreditNoteDialogConfirm(payload: CreditNoteDialogConfirm): void {
        if (!this.detail) {
            return;
        }
        localStorage.removeItem('orders');
        localStorage.removeItem('orderLines');
        localStorage.removeItem('routeParams');
        this.orderDraftService.clearDraft();
        const draftId = this.createDraftId();
        localStorage.setItem('routeParams', JSON.stringify(draftId));
        this.router.navigate(['/item-v2', draftId], {
            state: {
                creditNoteFromDispute: true,
                claimNumber: this.detail.claimNumber,
                sourceOrderNumber: this.detail.orderNumber,
                creditOrderTypeId: payload.creditOrderTypeId,
                seedLines: payload.seedLines
            }
        });
    }

    private createDraftId(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let id = '';
        for (let i = 0; i < 5; i++) {
            id += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return id;
    }

    openReviewDrawer(): void {
        if (!this.detail) return;
        if (this.drawerStatusOptions().length === 0) {
            this.messageService.add({
                severity: 'info',
                summary: 'No actions',
                detail: 'This dispute cannot be updated from its current status.'
            });
            return;
        }
        this.drawerVisible = true;
    }

    drawerStatusOptions(): { label: string; value: ReviewStatusOption }[] {
        const status = (this.detail?.status || '').toUpperCase();
        if (status === 'SUBMITTED') {
            return [
                { label: 'Approved', value: 'APPROVED' },
                { label: 'Rejected', value: 'REJECTED' },
                { label: 'Under Review', value: 'UNDER_REVIEW' }
            ];
        }
        if (status === 'UNDER_REVIEW' || status === 'PARTIALLY_APPROVED') {
            return [
                { label: 'Approved', value: 'APPROVED' },
                { label: 'Rejected', value: 'REJECTED' }
            ];
        }
        return [];
    }

    private apiErrorMessage(err: unknown): string {
        const httpErr = err as { status?: number; error?: unknown; message?: string };
        const body = httpErr?.error;
        if (body && typeof body === 'object' && body !== null && 'message' in body) {
            return String((body as { message: string }).message);
        }
        if (typeof body === 'string' && body.trim()) {
            return body;
        }
        if (httpErr?.status === 0) {
            return 'Network blocked (CORS) or Order Manager not reachable — restart backend on port 8444.';
        }
        if (httpErr?.status === 401) {
            return 'Session expired — please log in again.';
        }
        if (httpErr?.status === 409) {
            return 'This status change is not allowed for the current dispute state. Refresh the page and try again.';
        }
        return httpErr?.message || 'Please retry.';
    }

    submitDrawerUpdate(payload: {
        status: ReviewStatusOption | '';
        customerMessage: string;
        internalComment: string;
    }): void {
        if (!payload.status || !this.claimNumber) return;
        if (
            (payload.status === 'APPROVED' || payload.status === 'REJECTED') &&
            payload.customerMessage.trim().length < 10
        ) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Message required',
                detail: 'Customer message must be at least 10 characters for approve/reject.'
            });
            return;
        }

        this.loaderService.show('Updating dispute status...', 'Please wait...');
        this.disputeService
            .updateStatus(this.claimNumber, this.scope, {
                status: payload.status,
                customerMessage: payload.customerMessage.trim() || undefined,
                internalComment: payload.internalComment.trim() || undefined
            })
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (detail) => {
                    this.detail = detail;
                    this.drawerVisible = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Status updated',
                        detail: `Dispute moved to ${payload.status}`
                    });
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Failed to update status',
                        detail: this.apiErrorMessage(err)
                    });
                }
            });
    }

    onOpenAttachment(attachment: StaffDisputeAttachment): void {
        this.disputeService.downloadAttachment(this.claimNumber, attachment.id, this.scope).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Open failed',
                    detail: 'Could not open attachment.'
                });
            }
        });
    }

    onDownloadAttachment(attachment: StaffDisputeAttachment): void {
        this.disputeService.downloadAttachment(this.claimNumber, attachment.id, this.scope).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = attachment.originalFileName || 'attachment';
                a.click();
                URL.revokeObjectURL(url);
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Download failed',
                    detail: 'Could not download attachment.'
                });
            }
        });
    }

    private loadAttachmentPreviews(): void {
        if (!this.detail?.attachments?.length || !this.claimNumber) return;
        for (const attachment of this.detail.attachments) {
            if (!(attachment.contentType || '').toLowerCase().startsWith('image/')) continue;
            this.disputeService
                .downloadAttachment(this.claimNumber, attachment.id, this.scope)
                .subscribe({
                    next: (blob) => {
                        this.attachmentPreviewUrls.set(attachment.id, URL.createObjectURL(blob));
                    },
                    error: () => {}
                });
        }
    }

    private revokeAttachmentPreviews(): void {
        for (const url of this.attachmentPreviewUrls.values()) {
            URL.revokeObjectURL(url);
        }
        this.attachmentPreviewUrls.clear();
    }
}
